/**
 * `/o/:org/skills/:id` - the full page for one skill.
 *
 * V1 section 9 asks for Overview, Files, History and Improvements; a permission
 * summary; the current release; the checkpoint text; Use/Connect and Export; and
 * says a drawer may offer a quick preview with full-page editing here. This is
 * the full page, so the editor is a separate route and there is no drawer: a
 * drawer that holds the only copy of an editing surface is a trap on a narrow
 * screen, and section 9 separately requires full-page details at 390px.
 */

import type { AppShell, ScreenState } from '../app.js';
import * as api from '../api/queries.js';
import { type Result, ok } from '../api/client.js';
import { type RawHtml, html, raw } from '../lib/html.js';
import {
  DRAFT_STATE_LABEL,
  LIFECYCLE_LABEL,
  type PeopleDirectory,
  formatBytes,
  formatDateTime,
  personLabel,
  shortHash,
} from '../lib/format.js';
import { numericValue } from '../api/queries.js';
import { parseCheckpoints } from '../lib/review-model.js';
import { button, checkpoint, docHead, failurePanel, skeletonRows, tag } from '../ui/components.js';

type SkillTab = 'overview' | 'files' | 'history' | 'improvements';

const TAB_PARAM = 'tab';

export interface SkillData extends api.SkillDetail {
  readonly document: string | null;
  readonly tab: SkillTab;
  /**
   * This organization's groups, so an audience panel can name a group-derived
   * granting path.
   *
   * Loaded here because the Access panel is the only screen outside the admin
   * page that renders a `skill_grants` row, and `principal_id` is a GROUP id for
   * a `group`-kind grant. It is a `users`-shaped value, not a person, so the
   * people directory cannot answer it — and reading it through
   * `personLabel` (which is what this file did) is what put a truncated group
   * uuid where a group's name belongs.
   */
  readonly groups: readonly api.GroupRow[];
}

export async function loadSkillDetail(
  app: AppShell,
  orgId: string,
  skillId: string,
): Promise<Result<SkillData>> {
  const [detail, groups] = await Promise.all([
    api.getSkillDetail(app.client, orgId, skillId),
    api.listGroups(app.client, orgId),
  ]);
  if (!detail.ok) return detail;
  let document: string | null = null;
  if (detail.value.release !== null) {
    const bundle = await api.downloadBundle(app.client, skillId);
    document = bundle.ok ? bundle.value : null;
  }
  return ok({
    ...detail.value,
    document,
    tab: readTab(app.state.search),
    // A failed groups read leaves the audience's group paths unnamed rather
    // than failing the page: the skill itself is what this screen is for.
    groups: groups.ok ? groups.value : [],
  });
}

function readTab(search: string): SkillTab {
  const value = new URLSearchParams(search).get(TAB_PARAM);
  return value === 'files' || value === 'history' || value === 'improvements' ? value : 'overview';
}

export function renderSkillDetail(app: AppShell, screen: ScreenState): RawHtml {
  if (screen.kind === 'loading' || screen.kind === 'idle') {
    return html`
      ${docHead([{ label: 'Section', value: 'Skill' }], 'Loading')}
      <div class="panel">${skeletonRows(['Field', 'Value'], 6)}</div>
    `;
  }
  if (screen.kind === 'failed') {
    return html`
      ${docHead([{ label: 'Section', value: 'Skill' }], 'Skill')}
      ${failurePanel(screen.failure, { retry: 'skill:reload' })}
    `;
  }

  const data = screen.data as SkillData;
  const skill = data.skill;
  const isOwner = skill.owner_id === app.state.session?.userId;
  const deprecated = skill.lifecycle === 'deprecated';
  const checkpoints = parseCheckpoints(data.document ?? '');

  return html`
    ${docHead(
      [
        { label: 'Skill', value: skill.slug },
        { label: 'Revision', value: data.release?.revision ?? 'unpublished' },
        { label: 'State', value: LIFECYCLE_LABEL[skill.lifecycle] ?? skill.lifecycle },
      ],
      skill.title,
      skill.description ?? 'No description has been recorded for this skill.',
    )}

    <div class="btn-row">
      ${data.release === null
        ? button('This skill is unpublished', 'skill:noop', 'primary', { disabled: 'disabled', title: 'A skill with no current release cannot be used.' })
        : button('Use this skill', 'skill:export', 'primary', {
            // The one modal has to give focus back to whatever opened it
            // (V1 section 9), and the return address survives the re-render only
            // as an id. Measured 2026-10-01, real Chromium: with no id here,
            // Escape on the export dialog left `document.activeElement` as
            // `document.body`.
            id: 'use-this-skill',
          })}
      ${isOwner || data.grants.length > 0 ? button('Edit', 'skill:edit', 'secondary') : ''}
      <span class="topbar-spacer"></span>
      ${data.release === null ? '' : button('Download release', 'skill:download', 'secondary')}
    </div>

    ${deprecated
      ? html`<div class="callout callout-attention">
          <span class="callout-tag">Deprecated</span>
          <p>
            This skill is deprecated and serves with a warning.
            ${skill.replaced_by_skill_id === null
              ? 'No replacement has been named.'
              : html`It is replaced by
                  <a href="/o/${app.orgSlug}/skills/${skill.replaced_by_skill_id}"
                    >skill ${skill.replaced_by_skill_id.slice(0, 8)}</a
                  >.`}
          </p>
        </div>`
      : ''}
    ${data.warnings.map((warning) => html`<div class="callout" role="status"><p>${warning}</p></div>`)}

    <div class="btn-row">
      ${tabLink(app, data, 'overview', 'Overview')}
      ${tabLink(app, data, 'files', 'Files')}
      ${tabLink(app, data, 'history', 'History')}
      ${tabLink(app, data, 'improvements', 'Improvements')}
    </div>

    <div class="split">
      <div>${tabBody(app, data, checkpoints)}</div>
      <div>
        ${accessPanel(app, data)}
        ${lifecyclePanel(app, skill)}
        ${releasePanel(app.state.directory, data)}
        ${checkpointPanel(checkpoints)}
      </div>
    </div>
  `;
}

/**
 * The lifecycle controls: deprecate, and retire.
 *
 * TWO CONTROLS, AND ONLY ONE OF THEM AT A TIME
 *   `active -> deprecated` and `deprecated -> retired` are different
 *   consequences for whoever depends on this skill — the first keeps serving and
 *   starts warning, the second stops it being consumable at all — so they are
 *   two buttons whose labels name which is which, and only the one the recorded
 *   state permits is rendered. A single control that chose between them would be
 *   a control where one press can retire.
 *
 * NEITHER IS REVERSIBLE, AND THIS SAYS SO BESIDE THE BUTTON
 *   `app.set_skill_lifecycle` permits `active -> deprecated` and
 *   `deprecated -> retired` and nothing else; there is no tombstone and no
 *   reinstatement. A control that does not say so is a control that gets pressed
 *   once.
 *
 * WHO SEES IT
 *   Everybody who can open this skill sees the control, and the route refuses
 *   anyone without `manage` on it. Whether a person may deprecate is the
 *   DATABASE's answer (`authz.has_skill_permission(..., 'manage')`), and a
 *   second opinion rendered here could only disagree with it. What the screen
 *   can do honestly is offer the action and say what it will do.
 */
function lifecyclePanel(app: AppShell, skill: SkillData['skill']): RawHtml {
  const reason = html`
    <label class="field-label" for="lifecycle-reason">Reason</label>
    <textarea
      class="field"
      id="lifecycle-reason"
      rows="3"
      data-field="lifecycle.reason"
      placeholder="Why this skill is going away. This becomes the audit record."
    >${app.state.lifecycleReason}</textarea>
  `;

  if (skill.lifecycle === 'retired') {
    return html`
      <div class="panel">
        <div class="panel-head"><h2>Lifecycle</h2></div>
        <div class="panel-body stack-sm">
          <p class="cell-note">
            This skill is retired. It has left the catalog and can no longer be retrieved, by
            anyone — including by an agent holding a release id it cached earlier. Its history is
            retained, and nothing on this screen reverses it.
          </p>
        </div>
      </div>
    `;
  }

  if (skill.lifecycle === 'deprecated') {
    return html`
      <div class="panel">
        <div class="panel-head"><h2>Lifecycle</h2></div>
        <div class="panel-body stack-sm">
          <p class="cell-note">
            This skill is deprecated: it still serves, and every read of it carries a warning. It
            can be retired next, which stops it being retrievable at all.
          </p>
          ${reason}
          <div class="btn-row">
            ${button('Retire this skill', 'skill:lifecycle.retire', 'danger')}
          </div>
          <p class="cell-note">
            Retiring cannot be undone, and it will be refused unless you can manage this skill.
          </p>
        </div>
      </div>
    `;
  }

  return html`
    <div class="panel">
      <div class="panel-head"><h2>Lifecycle</h2></div>
      <div class="panel-body stack-sm">
        <p class="cell-note">
          This skill is active. Deprecating it keeps it serving and starts warning consumers that
          it is going away; it removes it from nothing.
        </p>
        ${reason}
        <div class="btn-row">
          ${button('Deprecate this skill', 'skill:lifecycle.deprecate', 'secondary')}
        </div>
        <p class="cell-note">
          Deprecating cannot be undone here, and it will be refused unless you can manage this
          skill.
        </p>
      </div>
    </div>
  `;
}

function tabLink(app: AppShell, data: SkillData, tab: SkillTab, label: string): RawHtml {
  if (data.tab === tab) {
    return html`<span class="tag tag-accent"><span class="glyph" aria-hidden="true">#</span>${label}</span>`;
  }
  void app;
  return button(label, `skill:tab:${tab}`, 'quiet');
}

function tabBody(app: AppShell, data: SkillData, checkpoints: readonly { id: string; line: number }[]): RawHtml {
  switch (data.tab) {
    case 'files':
      return filesPanel(data);
    case 'history':
      return historyPanel(data);
    case 'improvements':
      return improvementsPanel(app, data);
    case 'overview':
      return overviewPanel(data, checkpoints);
  }
}

function overviewPanel(data: SkillData, checkpoints: readonly { id: string; line: number }[]): RawHtml {
  return html`
    <div class="panel">
      <div class="panel-head"><h2>Overview</h2></div>
      <div class="panel-body stack-sm">
        <p>${data.skill.description ?? 'No description recorded.'}</p>
        <h3>When to use</h3>
        <p>
          The published document states when this skill applies. Open the document from Use this skill
          and read its opening section before relying on it.
        </p>
        ${checkpoints.length === 0
          ? ''
          : html`<h3>Human checkpoints declared</h3>
              ${checkpoints.map((note) =>
                checkpoint(
                  note.id,
                  'Declared in the published document at line ' +
                    String(note.line) +
                    '. Skills Manager shows where it is; it does not claim it was carried out.',
                ),
              )}`}
      </div>
    </div>
  `;
}

function filesPanel(data: SkillData): RawHtml {
  return html`
    <div class="panel">
      <div class="panel-head">
        <h2>Files in the published release</h2>
        <span class="cell-meta">${data.releaseFiles.length}</span>
      </div>
      <div class="panel-body">
        ${data.releaseFiles.length === 0
          ? html`<p class="cell-note">This skill has no published release, so there are no files to list.</p>`
          : html`<ul class="file-list">
              ${data.releaseFiles.map(
                (file) => html`<li>
                  <span>${file.path}</span>
                  <span>${formatBytes(numericValue(file.bytes))} · ${shortHash(file.content_hash)}</span>
                </li>`,
              )}
            </ul>`}
      </div>
    </div>
  `;
}

function historyPanel(data: SkillData): RawHtml {
  return html`
    <div class="panel">
      <div class="panel-head"><h2>Draft history</h2></div>
      <div class="panel-body">
        ${data.drafts.length === 0
          ? html`<p class="cell-note">No draft has been written for this skill.</p>`
          : data.drafts.map(
              (draft) => html`<div class="record">
                <div class="record-head">
                  <strong>${DRAFT_STATE_LABEL[draft.state] ?? draft.state}</strong>
                  <span class="cell-meta">version ${draft.version}</span>
                </div>
                <span class="cell-note">${draft.change_reason ?? 'No change reason recorded.'}</span>
                <span class="cell-meta">${formatDateTime(draft.updated_at)}</span>
              </div>`,
            )}
      </div>
    </div>
  `;
}

function improvementsPanel(app: AppShell, data: SkillData): RawHtml {
  const open = data.proposals.filter((row) => row.status === 'open');
  return html`
    <div class="panel">
      <div class="panel-head">
        <h2>Proposals against this skill</h2>
        <span class="cell-meta">${open.length} open</span>
      </div>
      <div class="panel-body">
        ${data.proposals.length === 0
          ? html`<p class="cell-note">Nothing has been proposed against this skill.</p>`
          : data.proposals.map(
              (proposal) => html`<div class="record">
                <div class="record-head">
                  <strong>${proposal.category}</strong>
                  ${tag(proposal.status, 'P', proposal.status === 'open' ? 'attention' : 'plain')}
                </div>
                <p>${proposal.summary}</p>
                <span class="cell-meta">by ${personLabel(proposal.reporter_id, app.state.directory)} · ${formatDateTime(proposal.created_at)}</span>
              </div>`,
            )}
        <div class="btn-row">
          ${button('All improvements', `skill:goto-improvements`, 'secondary')}
          <span class="cell-meta">${app.orgSlug}</span>
        </div>
      </div>
    </div>
  `;
}

/**
 * One audience row, named by what the principal actually IS.
 *
 * `skill_grants.principal_id` is a uuid of three different things depending on
 * `principal_kind` (`P1-CONTRACT.md` §3), and the panel used to render all
 * three through `personLabel`:
 *
 *   user:  a person              -> the address the identity provider holds
 *   group: `app.groups.id`       -> the group's name
 *   org:   the organization's id -> the organization's name
 *
 * Passing a group id to a people lookup does not produce a degraded name, it
 * produces a category error: the directory holds no such person, so the answer
 * would be "Unknown person" beside a group whose name is one read away. Hence
 * the branch on the kind the row itself carries.
 *
 * The kind is still printed, because "who" without "by what route" is not an
 * explanation an administrator can act on, and ACL-03 is a clause about
 * exactly that.
 */
function principalLabel(kind: string, id: string, data: SkillData, app: AppShell): string {
  if (kind === 'group') {
    const group = data.groups.find((row) => row.id === id);
    return `group:${group === undefined ? 'unnamed group' : group.name}`;
  }
  if (kind === 'org') {
    return `org:${app.state.organization?.name ?? 'this organization'}`;
  }
  return `user:${personLabel(id, app.state.directory)}`;
}

/**
 * The permission summary.
 *
 * V1 section 9 requires this and it is deliberately modest. The audience rows
 * are only readable by a manager (`skill_grants_read` requires `manage`), so an
 * empty list means "you may not see the audience", which is stated rather than
 * rendered as "no audience".
 */
function accessPanel(app: AppShell, data: SkillData): RawHtml {
  const isOwner = data.skill.owner_id === app.state.session?.userId;
  return html`
    <div class="panel">
      <div class="panel-head"><h2>Access</h2></div>
      <div class="panel-body stack-sm">
        <dl class="dl">
          <dt>Owner</dt>
          <dd>${personLabel(data.skill.owner_id, app.state.directory)}</dd>
          <dt>Your access</dt>
          <dd>${isOwner ? 'Owner' : 'Granted by an administrator'}</dd>
          <dt>Audience</dt>
          <dd>
            ${data.grants.length === 0
              ? 'Not visible to you. Only a manager can read a skill audience.'
              : `${data.grants.length} granting ${data.grants.length === 1 ? 'path' : 'paths'}`}
          </dd>
        </dl>
        ${data.grants.length === 0
          ? ''
          : html`<ul class="file-list">
              ${data.grants.map(
                (grant) => html`<li>
                  <span>${principalLabel(grant.principal_kind, grant.principal_id, data, app)}</span>
                  <span>${grant.permission}</span>
                </li>`,
              )}
            </ul>`}
      </div>
    </div>
  `;
}

function releasePanel(directory: PeopleDirectory, data: SkillData): RawHtml {
  const release = data.release;
  return html`
    <div class="panel">
      <div class="panel-head"><h2>Current release</h2></div>
      <div class="panel-body">
        ${release === null
          ? html`<p class="cell-note">
              Unpublished. This skill has no current release, so an agent cannot retrieve it.
            </p>`
          : html`<dl class="dl">
              <dt>Revision</dt>
              <dd>${release.revision}</dd>
              <dt>Published</dt>
              <dd>${formatDateTime(release.published_at)}</dd>
              <dt>By</dt>
              <dd>${personLabel(release.published_by, directory)}</dd>
              <dt>Manifest digest</dt>
              <dd><code>${release.manifest_digest}</code></dd>
              ${release.changelog === null
                ? ''
                : html`<dt>Changelog</dt><dd>${release.changelog}</dd>`}
            </dl>`}
      </div>
    </div>
  `;
}

function checkpointPanel(checkpoints: readonly { id: string; line: number }[]): RawHtml {
  if (checkpoints.length === 0) return raw('');
  return html`
    <div class="panel">
      <div class="panel-head"><h2>Checkpoints</h2></div>
      <div class="panel-body">
        ${checkpoints.map((note) =>
          checkpoint(note.id, `Declared at line ${note.line}. Shown as defined, never as carried out.`),
        )}
      </div>
    </div>
  `;
}